import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

import type {
  BudgetInfo,
  BudgetPart,
  Device,
  Photo,
  ReceptionDraft,
  ReceptionEvidenceStatus,
  ReceptionSignatureMeta,
  ReceptionStartRepairStatus,
  ReceptionWizardState,
} from '@/types/app/reception';

/**
 * Crea un draft vacío listo para iniciar el wizard.
 */
const createEmptyDraft = (): ReceptionDraft => ({
  id: nanoid(),
  step: 'client',
  isDirty: false,
  client: {
    name: '',
    phoneNumber: '',
    remoteId: undefined,
  },
  signature: undefined,
  devices: {
    byId: {},
    allIds: [],
    activeDeviceId: undefined,
  },
  photos: {
    byId: {},
    allIds: [],
  },
  idempotency: {},
});

const initialState: ReceptionWizardState = {
  activeDraftId: undefined,
  draftsById: {},
};

/**
 * Obtiene el draft activo del estado (si existe).
 */
const getActiveDraft = (state: ReceptionWizardState): ReceptionDraft | undefined => {
  if (!state.activeDraftId) return undefined;
  return state.draftsById[state.activeDraftId];
};

/**
 * Carga útil para insertar o actualizar un dispositivo.
 * - `Partial<Device>` permite enviar sólo los campos que vas a cambiar.
 * - `id` es opcional: si no viene, normalmente se genera (ej. con nanoid()).
 */
export type UpsertDevicePayload = Partial<Device> & { id?: string };

/**
 * Asegura que el dispositivo tenga objeto de presupuesto.
 * Si no existe, lo inicializa con valores por defecto.
 *
 * @param device Dispositivo al que se le valida/crea el presupuesto
 * @returns El objeto BudgetInfo existente o recién creado
 */
const ensureBudget = (device: Device): BudgetInfo => {
  if (!device.budget) {
    device.budget = {
      serviceCost: 0, // costo del servicio (mano de obra, diagnóstico, etc.)
      advance: 0, // anticipo entregado por el cliente
      total: 0, // total calculado (serviceCost + partes - descuentos…)
      parts: [], // piezas del presupuesto (BudgetPart[])
    };
  }
  return device.budget;
};

/**
 * Garantiza que el bloque remoto exista en el dispositivo antes de mutarlo.
 */
const ensureRemote = (device: Device) => {
  if (!device.remote) {
    device.remote = {
      signatureDone: false,
      photosDone: false,
      paymentDone: false,
      partsDone: false,
      evidenceStatus: undefined,
      evidenceReplay: false,
      evidencePhotosReplay: false,
      evidenceSignatureReplay: false,
      evidenceMessage: null,
      startRepairStatus: undefined,
      startRepairOrderStatusId: undefined,
      startRepairFailedPartsCount: 0,
      startRepairMessage: null,
    };
  } else {
    device.remote.signatureDone ??= false; // (typeof device.remote.signatureDone === 'undefined') {
    device.remote.photosDone ??= false;
    device.remote.paymentDone ??= false;
    device.remote.partsDone ??= false;
    device.remote.evidenceStatus ??= undefined;
    device.remote.evidenceReplay ??= false;
    device.remote.evidencePhotosReplay ??= false;
    device.remote.evidenceSignatureReplay ??= false;
    device.remote.evidenceMessage ??= null;
    device.remote.startRepairStatus ??= undefined;
    device.remote.startRepairOrderStatusId ??= undefined;
    device.remote.startRepairFailedPartsCount ??= 0;
    device.remote.startRepairMessage ??= null;
  }
  return device.remote;
};

/**
 * Inserta un valor en un arreglo sólo si no existe previamente.
 * Útil para mantener listas de IDs sin duplicados (orden estable).
 *
 * @param collection Arreglo de strings (IDs)
 * @param value ID a insertar si no existe
 */
const pushUnique = (collection: string[], value: string) => {
  if (!collection.includes(value)) {
    collection.push(value);
  }
};

/**
 * Elimina un valor del arreglo manteniendo el orden del resto.
 * No falla si el valor no existe.
 *
 * @param collection Arreglo de strings (IDs)
 * @param value ID a eliminar
 */
const removeFromOrder = (collection: string[], value: string) => {
  const index = collection.indexOf(value);
  if (index >= 0) collection.splice(index, 1);
};

/**
 * Desacopla una foto del borrador:
 * - La elimina del índice global de fotos (byId y allIds)
 * - La quita de la lista de fotos del dispositivo al que pertenecía
 *
 * @param draft Borrador de la recepción (contiene devices y photos normalizados)
 * @param photoId ID de la foto a remover
 */
const detachPhoto = (draft: ReceptionDraft, photoId: string) => {
  const photo = draft.photos.byId[photoId];
  if (!photo) return; // Nada que hacer si la foto no existe

  // Quitar la referencia en el dispositivo dueño de la foto
  const device = draft.devices.byId[photo.deviceId];
  if (device) {
    device.photos = device.photos.filter((item) => item !== photoId);
  }

  // Eliminar del índice global de fotos
  delete draft.photos.byId[photoId];
  removeFromOrder(draft.photos.allIds, photoId);
};

const receptionWizardSlice = createSlice({
  name: 'receptionWizard',
  initialState,
  reducers: {
    /**
     * Inicia un nuevo draft y lo marca como activo.
     */
    startDraft(state) {
      const draft = createEmptyDraft();
      state.activeDraftId = draft.id;
      state.draftsById[draft.id] = draft;
    },
    /**
     * Resetea el draft activo manteniendo su id,
     * útil cuando se reinicia el wizard sin salir del módulo.
     */
    resetActiveDraft(state) {
      if (!state.activeDraftId) return;
      const currentId = state.activeDraftId;
      const freshDraft = createEmptyDraft();
      freshDraft.id = currentId;
      state.draftsById[currentId] = freshDraft;
    },
    /**
     * Cambia el paso visible del wizard.
     */
    setStep(state, action: PayloadAction<ReceptionDraft['step']>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.step = action.payload;
    },
    /**
     * Mezcla datos del cliente dentro del draft activo.
     */
    setClient(state, action: PayloadAction<Partial<ReceptionDraft['client']>>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.client = { ...draft.client, ...action.payload };
      draft.isDirty = true;
    },
    setSignatureMeta(state, action: PayloadAction<ReceptionSignatureMeta | undefined>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.signature = action.payload;
      draft.isDirty = true;
    },
    /**
     * Inserta o sobrescribe un dispositivo y lo deja como activo.
     */
    upsertDevice(state, action: PayloadAction<UpsertDevicePayload>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const id = action.payload.id ?? nanoid();
      const existing = draft.devices.byId[id];
      // Base seguro (no hagas spread de undefined)
      const base: Device = existing ?? {
        id,
        kindId: '',
        brandId: '',
        modelId: '',
        photos: [],
      };

      // Construye el siguiente objeto sin duplicaciones confusas
      const nextDevice: Device = {
        ...base,
        ...action.payload, // puede traer brandId, modelId, kindId, photos, etc.
        id, // siempre fuerza el id calculado
        photos: action.payload.photos ?? base.photos, // última palabra sobre fotos
      };
      draft.devices.byId[id] = nextDevice;
      pushUnique(draft.devices.allIds, id);
      draft.devices.activeDeviceId = id;
      draft.isDirty = true;
    },
    /**
     * Actualiza un dispositivo existente.
     */
    updateDevice(state, action: PayloadAction<{ id: string; changes: Partial<Device> }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const current = draft.devices.byId[action.payload.id];
      if (!current) return;
      draft.devices.byId[action.payload.id] = { ...current, ...action.payload.changes };
      draft.isDirty = true;
    },
    /**
     * Elimina un dispositivo y sus fotos asociadas.
     */
    removeDevice(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const id = action.payload;
      const device = draft.devices.byId[id];
      if (!device) return;
      device.photos.forEach((photoId) => detachPhoto(draft, photoId));
      delete draft.devices.byId[id];
      removeFromOrder(draft.devices.allIds, id);
      if (draft.devices.activeDeviceId === id) {
        draft.devices.activeDeviceId = draft.devices.allIds[0];
      }
      draft.isDirty = true;
    },
    /**
     * Define manualmente qué dispositivo se está editando.
     */
    setActiveDevice(state, action: PayloadAction<string | undefined>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      if (action.payload && !draft.devices.byId[action.payload]) return;
      draft.devices.activeDeviceId = action.payload;
    },
    /**
     * Registra una foto ligada a un dispositivo concreto.
     */
    addPhoto(
      state,
      action: PayloadAction<{ deviceId: string; photo?: Partial<Photo> & { id?: string } }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      if (!device) return;
      const id = action.payload.photo?.id ?? nanoid();
      const slotIndex =
        typeof action.payload.photo?.slotIndex === 'number' ? action.payload.photo.slotIndex : null;
      const photo: Photo = {
        id,
        deviceId: action.payload.deviceId,
        mime: action.payload.photo?.mime ?? 'image/jpeg',
        status: action.payload.photo?.status ?? 'local',
        ...action.payload.photo,
        slotIndex,
      };
      draft.photos.byId[id] = photo;
      pushUnique(draft.photos.allIds, id);
      pushUnique(device.photos, id);
      draft.isDirty = true;
    },
    /**
     * Actualiza metadata/estado de una foto.
     */
    updatePhoto(state, action: PayloadAction<{ id: string; changes: Partial<Photo> }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const photo = draft.photos.byId[action.payload.id];
      if (!photo) return;
      draft.photos.byId[action.payload.id] = { ...photo, ...action.payload.changes };
      draft.isDirty = true;
    },
    /**
     * Elimina una foto y la desvincula del dispositivo correspondiente.
     */
    removePhoto(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      detachPhoto(draft, action.payload);
      draft.isDirty = true;
    },
    /**
     * Sustituye completamente el presupuesto de un dispositivo.
     */
    setBudget(state, action: PayloadAction<{ deviceId: string; budget: BudgetInfo }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      if (!device) return;
      device.budget = action.payload.budget;
      draft.isDirty = true;
    },
    /**
     * Limpia el presupuesto de un dispositivo (sin tocar el resto del draft).
     */
    resetBudget(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload];
      if (!device) return;
      device.budget = undefined;
      draft.isDirty = true;
    },
    /**
     * Agrega una pieza al presupuesto del dispositivo indicado.
     */
    addBudgetPart(
      state,
      action: PayloadAction<{ deviceId: string; part: Omit<BudgetPart, 'id'> & { id?: string } }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      if (!device) return;
      const budget = ensureBudget(device);
      const id = action.payload.part.id ?? nanoid();
      const nextPart: BudgetPart = {
        ...action.payload.part,
        id,
      };
      budget.parts.push(nextPart);
      draft.isDirty = true;
    },
    /**
     * Actualiza una pieza existente dentro del presupuesto.
     */
    updateBudgetPart(
      state,
      action: PayloadAction<{
        deviceId: string;
        partId: string;
        changes: Partial<BudgetPart>;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      const parts = device?.budget?.parts;
      if (!parts) return;
      const part = parts.find((item) => item.id === action.payload.partId);
      if (!part) return;
      Object.assign(part, action.payload.changes);
      draft.isDirty = true;
    },
    /**
     * Quita una pieza del presupuesto.
     */
    removeBudgetPart(state, action: PayloadAction<{ deviceId: string; partId: string }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      if (!device?.budget) return;
      device.budget.parts = device.budget.parts.filter((part) => part.id !== action.payload.partId);
      draft.isDirty = true;
    },
    /**
     * Define la clave de idempotencia vigente antes de enviar datos.
     */
    setIdempotencyKey(state, action: PayloadAction<string | undefined>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.idempotency.current = action.payload;
    },
    /**
     * Mueve la clave actual al historial tras un envío exitoso.
     */
    commitIdempotencyKey(state) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      if (draft.idempotency.current) {
        draft.idempotency.last = draft.idempotency.current;
        draft.idempotency.current = undefined;
      }
    },
    /**
     * Guarda el id remoto del cliente creado/seleccionado en backend.
     */
    setClientRemoteId(state, action: PayloadAction<number | undefined>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.client.remoteId = action.payload;
    },
    /**
     * Sincroniza metadatos remotos (orderId/codigo) para un dispositivo específico.
     */
    setDeviceOrderMeta(
      state,
      action: PayloadAction<{ deviceId: string; orderId: number; orderCode?: string | null }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload.deviceId];
      if (!device) return;
      const remote = ensureRemote(device);
      remote.orderId = action.payload.orderId;
      remote.orderCode = action.payload.orderCode ?? null;
    },
    markOrderSignatureDone(state, action: PayloadAction<number>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device?.remote) return;
        if (device.remote.orderId === action.payload) {
          device.remote.signatureDone = true;
        }
      });
    },
    markOrderPhotosDone(state, action: PayloadAction<number>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device?.remote) return;
        if (device.remote.orderId === action.payload) {
          device.remote.photosDone = true;
        }
      });
    },
    markOrderPaymentDone(state, action: PayloadAction<number>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device?.remote) return;
        if (device.remote.orderId === action.payload) {
          device.remote.paymentDone = true;
        }
      });
    },
    markOrderPartsDone(state, action: PayloadAction<{ orderId: number; hasParts: boolean }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device?.remote) return;
        if (device.remote.orderId === action.payload.orderId) {
          device.remote.partsDone = action.payload.hasParts;
        }
      });
    },
    setOrderEvidenceResult(
      state,
      action: PayloadAction<{
        orderId: number;
        status: ReceptionEvidenceStatus;
        replay?: boolean;
        photosReplay?: boolean;
        signatureReplay?: boolean;
        message?: string | null;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device) return;
        const remote = ensureRemote(device);
        if (remote.orderId === action.payload.orderId) {
          remote.evidenceStatus = action.payload.status;
          remote.evidenceReplay = action.payload.replay ?? false;
          remote.evidencePhotosReplay = action.payload.photosReplay ?? false;
          remote.evidenceSignatureReplay = action.payload.signatureReplay ?? false;
          remote.evidenceMessage = action.payload.message ?? null;
        }
      });
    },
    setOrderStartRepairResult(
      state,
      action: PayloadAction<{
        orderId: number;
        status: ReceptionStartRepairStatus;
        orderStatusId?: number | null;
        failedPartsCount?: number;
        message?: string | null;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      Object.values(draft.devices.byId).forEach((device) => {
        if (!device) return;
        const remote = ensureRemote(device);
        if (remote.orderId === action.payload.orderId) {
          remote.startRepairStatus = action.payload.status;
          remote.startRepairOrderStatusId = action.payload.orderStatusId ?? null;
          remote.startRepairFailedPartsCount = action.payload.failedPartsCount ?? 0;
          remote.startRepairMessage = action.payload.message ?? null;
        }
      });
    },
    /**
     * Persistimos el intake registrado para reutilizarlo al mostrar tickets.
     */
    setLastIntakeMeta(state, action: PayloadAction<{ id: number; code?: string | null }>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.idempotency.lastIntake = action.payload;
    },
    /**
     * Limpia las piezas asociadas a un dispositivo (post asignación remota).
     */
    clearDeviceParts(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const device = draft.devices.byId[action.payload];
      if (!device?.budget) return;
      device.budget.parts = [];
    },
  },
});

export const {
  startDraft,
  resetActiveDraft,
  setStep,
  setClient,
  setSignatureMeta,
  upsertDevice,
  updateDevice,
  removeDevice,
  setActiveDevice,
  addPhoto,
  updatePhoto,
  removePhoto,
  setBudget,
  resetBudget,
  addBudgetPart,
  updateBudgetPart,
  removeBudgetPart,
  setIdempotencyKey,
  commitIdempotencyKey,
  setClientRemoteId,
  setDeviceOrderMeta,
  markOrderSignatureDone,
  markOrderPhotosDone,
  markOrderPaymentDone,
  markOrderPartsDone,
  setOrderEvidenceResult,
  setOrderStartRepairResult,
  setLastIntakeMeta,
  clearDeviceParts,
} = receptionWizardSlice.actions;

export default receptionWizardSlice.reducer;
